/**
 * Analytics tracking service for page views and user behavior
 */

const API_URL = '';

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Track page entry time for session duration
let pageEntryTime = Date.now();

// Get device and browser info
const getDeviceInfo = () => {
  return {
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    user_agent: navigator.userAgent,
    referrer: document.referrer || ''
  };
};

/**
 * Track a page view
 * @param {string} page - Page name (landing, calculators, app, etc.)
 */
export const trackPageView = async (page) => {
  try {
    pageEntryTime = Date.now(); // Reset entry time
    
    const deviceInfo = getDeviceInfo();
    
    await fetch(`${API_URL}/api/v2/analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        page,
        session_id: getSessionId(),
        ...deviceInfo
      }),
    });
  } catch (error) {
    // Silently fail - analytics should not break the app
    console.debug('Analytics tracking failed:', error.message);
  }
};

/**
 * Track session end with time on page
 * @param {string} page - Page name
 */
export const trackSessionEnd = async (page) => {
  try {
    const timeOnPage = Math.round((Date.now() - pageEntryTime) / 1000); // seconds
    
    await fetch(`${API_URL}/api/v2/analytics/session-end`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        page,
        session_id: getSessionId(),
        time_on_page: timeOnPage
      }),
    });
  } catch (error) {
    console.debug('Analytics session end failed:', error.message);
  }
};

/**
 * Get analytics summary for admin dashboard
 * @param {number} days - Number of days to look back
 */
export const getAnalyticsSummary = async (days = 30) => {
  try {
    const response = await fetch(`${API_URL}/api/v2/analytics/summary?days=${days}`);
    if (!response.ok) throw new Error('Failed to fetch analytics');
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch analytics:', error);
    return null;
  }
};

/**
 * Get real-time analytics
 */
export const getRealtimeAnalytics = async () => {
  try {
    const response = await fetch(`${API_URL}/api/v2/analytics/realtime`);
    if (!response.ok) throw new Error('Failed to fetch realtime analytics');
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch realtime analytics:', error);
    return null;
  }
};

// Track page visibility changes for session duration
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // Use sendBeacon for reliable tracking when page is closing
      const timeOnPage = Math.round((Date.now() - pageEntryTime) / 1000);
      const data = JSON.stringify({
        page: window.currentAnalyticsPage || 'unknown',
        session_id: getSessionId(),
        time_on_page: timeOnPage
      });
      
      navigator.sendBeacon(`${API_URL}/api/v2/analytics/session-end`, data);
    }
  });
}

export default {
  trackPageView,
  trackSessionEnd,
  getAnalyticsSummary,
  getRealtimeAnalytics
};
